import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronCircleLeft, faPlus, faSave, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  PageContainer,
  SectionTitle,
  Button,
  Divider,
  fadeIn,
  Footer,
  useGoBack,
  Label,
  Input,
  Select,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TableFormGroup,
  SubSectionTitle,
  ButtonSuccess,
} from '../styles/PageStyles';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/common/ToastProvider';
import { createModel, getModelByCode, updateModel } from '../services/modelHubService';
import { Loader } from '../components/common/Loader';

const PROVIDERS = {
  azure: { label: 'Azure OpenAI', id: 'azure_openai', color: '#4C8DFF', disabled: false },
  anthropic: { label: 'Anthropic', id: 'anthropic', color: '#E08D6D', disabled: false },
  google: { label: 'Google', id: 'google', color: '#4285F4', disabled: true },
  ollama: { label: 'Ollama', id: 'ollama', color: '#B79BFF', disabled: true },
};

const PROVIDER_ICONS = {
  azure: '/assets/images/models/azure_openai.png',
  anthropic: '/assets/images/models/Anthropic-Logo.webp',
  google: '/assets/images/models/google-color.png',
  ollama: '/assets/images/models/ollama-logo.png',
};

const MODEL_MAP = {
  azure: ['gpt-4o', 'gpt-5'],
  anthropic: ['Claude Sonnet', 'Claude Opus'],
};

const TYPE_OPTIONS = ['Chat', 'Reasoning', 'Embedding', 'Vision'];

function providerKeyFromLabel(label) {
  if (!label) return null;
  if (/azure/i.test(label)) return 'azure';
  if (/anthropic/i.test(label)) return 'anthropic';
  return null;
}

function normalizeProviderForAPI(provider) {
  if (!provider) return '';
  const map = { 'Azure OpenAI': 'azure_openai', Anthropic: 'anthropic' };
  return map[provider] || provider.toLowerCase().replace(/\s+/g, '_');
}

function normalizeProviderFromAPI(provider) {
  if (!provider) return '';
  const m = {
    azure_openai: 'Azure OpenAI',
    'azure openai': 'Azure OpenAI',
    anthropic: 'Anthropic',
  };
  const key = String(provider).toLowerCase();
  return m[key] || provider;
}

function normalizeTypeLabel(t) {
  if (!t) return '';
  const x = String(t).toLowerCase();
  if (x === 'chat') return 'Chat';
  if (x === 'reasoning') return 'Reasoning';
  if (x === 'embedding') return 'Embedding';
  if (x === 'vision') return 'Vision';
  return t;
}

function supportsType(providerKey, type) {
  if (providerKey === 'anthropic' && type === 'Embedding') return false;
  return true;
}

function getParamSchema(providerKey, type) {
  if (providerKey === 'azure') {
    if (type === 'Embedding') {
      return [
        { key: 'id', label: 'Model ID', kind: 'text', default: 'text-embedding-3-small', desc: 'This must match the deployed embedding model id' },
        { key: 'dimensions', label: 'Dimensions', kind: 'int', default: 1536, desc: 'Output embedding vector size' },
        { key: 'encoding_format', label: 'Encoding Format', kind: 'select', options: ['float', 'base64'], default: 'float', desc: 'Return format for vectors' },
        { key: 'user', label: 'User', kind: 'text', default: '', desc: 'End-user identifier' },
        { key: 'api_version', label: 'API Version', kind: 'text', default: '2024-10-21', desc: 'Azure API version' },
        { key: 'azure_endpoint', label: 'Azure Endpoint', kind: 'text', default: '', desc: 'Your Azure OpenAI resource endpoint' },
        { key: 'azure_deployment', label: 'Azure Deployment', kind: 'text', default: '', desc: 'Azure deployment name/alias' },
        { key: 'base_url', label: 'Base URL', kind: 'text', default: '', desc: 'Optional base URL override' },
        { key: 'azure_ad_token', label: 'Azure AD Token', kind: 'text', default: '', desc: 'Use AAD token instead of API key (optional)' },
        { key: 'organization', label: 'Organization', kind: 'text', default: '', desc: 'Optional organization id' },
      ];
    }
    if (type === 'Vision') {
      return [
        { key: 'id', label: 'Model ID', kind: 'text', default: 'gpt-4o', desc: 'Base model id for the vision deployment' },
        { key: 'api_key', label: 'API Key', kind: 'text', default: '', desc: 'Azure OpenAI API key' },
        { key: 'encoding_format', label: 'Encoding Format', kind: 'select', options: ['float', 'base64'], default: 'float', desc: 'Return format for any vectors' },
        { key: 'user', label: 'User', kind: 'text', default: '', desc: 'End-user identifier' },
        { key: 'api_version', label: 'API Version', kind: 'text', default: '2024-10-21', desc: 'Azure API version' },
        { key: 'azure_endpoint', label: 'Azure Endpoint', kind: 'text', default: '', desc: 'Your Azure OpenAI resource endpoint' },
        { key: 'azure_deployment', label: 'Azure Deployment', kind: 'text', default: '', desc: 'Azure deployment name/alias' },
        { key: 'base_url', label: 'Base URL', kind: 'text', default: '', desc: 'Optional base URL override' },
        { key: 'azure_ad_token', label: 'Azure AD Token', kind: 'text', default: '', desc: 'Use AAD token instead of API key (optional)' },
        { key: 'organization', label: 'Organization', kind: 'text', default: '', desc: 'Optional organization id' },
      ];
    }
    const chatReason = [
      { key: 'max_output_tokens', label: 'Max Output Tokens', kind: 'int', default: 4096, desc: 'Upper bound on generated tokens' },
      { key: 'max_tool_calls', label: 'Max Tool Calls', kind: 'int', default: 10, desc: 'Cap on tool calls per response' },
      { key: 'parallel_tool_calls', label: 'Parallel Tool Calls', kind: 'bool', default: true, desc: 'Allow multiple tool calls per turn' },
      { key: 'verbosity', label: 'Verbosity', kind: 'select', options: ['low', 'medium', 'high'], default: null, desc: 'Length / detail of the final answer' },
      { key: 'reasoning_effort', label: 'Reasoning Effort', kind: 'select', options: ['minimal', 'low', 'medium', 'high'], default: null, desc: 'Depth of internal reasoning' },
      { key: 'reasoning_summary', label: 'Reasoning Summary', kind: 'select', options: ['auto', 'concise', 'detailed'], default: null, desc: 'Summary detail for the reasoning trace' },
      { key: 'store', label: 'Store Response', kind: 'bool', default: false, desc: 'Persist the response for later retrieval' },
      { key: 'temperature', label: 'Temperature', kind: 'float', default: 0.7, desc: 'Controls randomness of output' },
      { key: 'top_p', label: 'Top P', kind: 'float', default: 1, desc: 'Nucleus sampling threshold' },
      { key: 'truncation', label: 'Truncation', kind: 'select', options: ['auto', 'disabled'], default: 'auto', desc: 'Context truncation strategy' },
      { key: 'user', label: 'User', kind: 'text', default: '', desc: 'End-user identifier for abuse monitoring' },
      { key: 'service_tier', label: 'Service Tier', kind: 'select', options: ['auto', 'default', 'flex', 'priority'], default: 'auto', desc: 'Latency / cost tier for this request' },
      { key: 'strict_output', label: 'Strict Output', kind: 'bool', default: true, desc: 'Guarantee schema adherence for structured outputs' },
      { key: 'background', label: 'Background Mode', kind: 'bool', default: false, desc: 'Run as a polled, long-running background task' },
      { key: 'background_poll_interval', label: 'Background Poll Interval', kind: 'float', default: 2.0, desc: 'Polling interval in seconds' },
      { key: 'background_max_wait', label: 'Background Max Wait', kind: 'float', default: 600.0, desc: 'Maximum background wait in seconds' },
      { key: 'api_key', label: 'API Key', kind: 'text', default: '', desc: 'Azure OpenAI API key' },
      { key: 'organization', label: 'Organization', kind: 'text', default: '', desc: 'Optional organization id' },
      { key: 'base_url', label: 'Base URL', kind: 'text', default: '', desc: 'Optional base URL override' },
      { key: 'timeout', label: 'Timeout (s)', kind: 'float', default: 60, desc: 'Request timeout in seconds' },
      { key: 'max_retries', label: 'Max Retries', kind: 'int', default: 2, desc: 'Retry attempts on failure' },
    ];
    return chatReason;
  }
  if (providerKey === 'anthropic') {
    return [
      { key: 'max_tokens', label: 'Max Tokens', kind: 'int', default: 8192, desc: 'Upper bound on generated tokens' },
      { key: 'temperature', label: 'Temperature', kind: 'float', default: 1, desc: 'Controls randomness of output' },
      { key: 'top_p', label: 'Top P', kind: 'float', default: 1, desc: 'Nucleus sampling threshold' },
      { key: 'top_k', label: 'Top K', kind: 'int', default: 0, desc: 'Restrict sampling to top K tokens' },
      { key: 'cache_system_prompt', label: 'Cache System Prompt', kind: 'bool', default: false, desc: 'Enable prompt caching on the system block' },
      { key: 'extended_cache_time', label: 'Extended Cache Time', kind: 'bool', default: false, desc: 'Use longer prompt-cache TTL' },
      { key: 'cache_tools', label: 'Cache Tools', kind: 'bool', default: false, desc: 'Enable prompt caching on the tools block' },
      { key: 'citations', label: 'Citations', kind: 'bool', default: true, desc: 'Attach citations to document blocks' },
      { key: 'append_trailing_user_message', label: 'Append Trailing User Message', kind: 'bool', default: false, desc: 'Append a user turn if the conversation ends on assistant' },
      { key: 'trailing_user_message_content', label: 'Trailing User Message Content', kind: 'text', default: 'continue', desc: 'Content for the appended user message' },
      { key: 'api_key', label: 'API Key', kind: 'text', default: '', desc: 'Anthropic API key' },
      { key: 'auth_token', label: 'Auth Token', kind: 'text', default: '', desc: 'Bearer token (optional)' },
      { key: 'timeout', label: 'Timeout (s)', kind: 'float', default: 60, desc: 'Request timeout in seconds' },
    ];
  }
  return [];
}

export default function ModelHubDetailsV2() {
  const navigate = useNavigate();
  const { modelId } = useParams();
  const goBack = useGoBack();
  const { user } = useAuth();
  const { showToast } = useToast();

  const [id, setId] = useState(undefined);
  const [code, setCode] = useState(undefined);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(modelId !== 'new');
  const [provider, setProvider] = useState('');
  const [type, setType] = useState('');
  const [model, setModel] = useState('');
  const [deploymentName, setDeploymentName] = useState('');
  const [version, setVersion] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  const [displayName, setDisplayName] = useState('');

  const [tempParamValues, setTempParamValues] = useState({});
  const [customParams, setCustomParams] = useState([]);
  const [search, setSearch] = useState('');

  const providerKey = useMemo(() => providerKeyFromLabel(provider), [provider]);
  const modelNames = useMemo(() => (providerKey ? MODEL_MAP[providerKey] || [] : []), [providerKey]);
  const paramSchema = useMemo(() => (providerKey && type ? getParamSchema(providerKey, type) : []), [providerKey, type]);
  const allParams = useMemo(() => [...paramSchema, ...customParams], [paramSchema, customParams]);
  const hasProvider = !!providerKey;
  const hasType = !!type;
  const hasModel = !!model;

  useEffect(() => {
    if (isEditMode) return; // preserve loaded values when editing
    setTempParamValues({});
    setCustomParams([]);
    setModel('');
    setDeploymentName('');
  }, [providerKey, type, isEditMode]);

  // Load existing model when editing
  useEffect(() => {
    const load = async () => {
      if (!modelId || modelId === 'new') return;
      try {
        setLoading(true);
        const resp = await getModelByCode(modelId);
        if (!resp) return;
        setIsEditMode(true);
        setId(resp.id);
        setCode(resp.code);
        setProvider(normalizeProviderFromAPI(resp.provider));
        setType(normalizeTypeLabel(resp.model_type || resp.type || ''));
        setModel(resp.name || '');
        setVersion(resp.config_params?.api_version || '');
        setStatus((resp.status && String(resp.status).toUpperCase()) || 'ACTIVE');
        setDisplayName(resp.displayName || '');
        // Prefer existing deployment name from config if present
        const cfg = resp.config_params || {};
        setDeploymentName(cfg.id || cfg.azure_deployment || resp.displayName || '');
        // After provider/type are set, apply params
        setTimeout(() => setTempParamValues(cfg), 0);
      } catch (e) {
        console.error('Failed to load model', e);
        showToast({ message: 'Error loading model', type: 'error' });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [modelId, showToast]);

  const filteredParams = allParams.filter(p => (p.label?.toLowerCase().includes(search.toLowerCase()) || p.key.toLowerCase().includes(search.toLowerCase())));

  const handleParamChange = (k, v) => setTempParamValues(prev => ({ ...prev, [k]: v }));

  const addCustomParam = () => {
    const key = `custom_${Date.now()}`;
    const newSchema = { key, label: 'Custom Parameter', kind: 'text', default: '' };
    setCustomParams(prev => [...prev, newSchema]);
    setTempParamValues(prev => ({ ...prev, [key]: '' }));
  };

  const updateCustomLabel = (key, label) => {
    setCustomParams(prev => prev.map(p => p.key === key ? { ...p, label } : p));
  };

  const removeParam = (key) => {
    if (key.startsWith('custom_')) {
      setCustomParams(prev => prev.filter(p => p.key !== key));
    }
    setTempParamValues(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const buildConfig = () => {
    const cfg = {};
    // include schema params when set
    paramSchema.forEach(def => {
      const v = tempParamValues[def.key];
      if (v !== undefined && v !== '') cfg[def.key] = v;
    });
    // include custom params
    Object.keys(tempParamValues)
      .filter(k => k.startsWith('custom_'))
      .forEach(k => { if (tempParamValues[k] !== '') cfg[k] = tempParamValues[k]; });
    // force-include connection keys regardless of schema
    ['api_key', 'base_url', 'organization'].forEach(k => {
      const v = tempParamValues[k];
      if (v !== undefined && v !== '') cfg[k] = v;
    });
    // fallback: if base_url missing but azure_endpoint exists, mirror it
    if (!cfg.base_url && tempParamValues['azure_endpoint']) cfg.base_url = tempParamValues['azure_endpoint'];
    // required mappings
    if (deploymentName) cfg.id = deploymentName;
    if (version) cfg.api_version = version;
    return cfg;
  };

  // Keep version <-> api_version synchronized when user edits the param row directly
  useEffect(() => {
    const apiV = tempParamValues['api_version'];
    if (apiV !== undefined && apiV !== version) {
      setVersion(apiV);
    }
  }, [tempParamValues, version]);

  const save = async () => {
    try {
      if (!provider) { showToast({ message: 'Select a provider', type: 'error' }); return; }
      if (!type) { showToast({ message: 'Select a model type', type: 'error' }); return; }
      if (!model) { showToast({ message: 'Select a model name', type: 'error' }); return; }
      if (!deploymentName) { showToast({ message: 'Enter deployment name', type: 'error' }); return; }

      const config_params = buildConfig();
      const iconFile = (PROVIDER_ICONS[providerKey] || '').split('/').pop() || '';
      const basePayload = {
        code: code || ('MDL-' + new Date().getTime()),
        company_cd: user?.companyCd,
        name: model,
        model_type: type,
        status,
        latency: '',
        version: 1,
        provider: normalizeProviderForAPI(provider),
        performance: 0,
        costPerToken: '$0.00010',
        displayName: displayName || '',
        description: '',
        icon: iconFile,
        config_params,
      };

      if (isEditMode && id) {
        await updateModel({ id, ...basePayload });
        showToast({ message: 'Model updated successfully', type: 'success' });
      } else {
        await createModel(basePayload);
        showToast({ message: 'Model created successfully', type: 'success' });
      }
      navigate('/model-hub');
    } catch (e) {
      showToast({ message: e.message || 'Error creating model', type: 'error' });
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <Loader />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <motion.div initial="hidden" animate="visible" variants={fadeIn}>
        <style>{`
          .shell{position:relative;max-width:1240px;margin:0 auto}
          .panel{background:white;border:1px solid #ccc;border-radius:10px;padding:22px;margin-bottom:20px}
          .section-label{font-size:11px;text-transform:uppercase;letter-spacing:.12em;color:var(--text-secondary);margin:0 0 12px;display:flex;align-items:center;gap:8px}
          .n{color:var(--primary-500)}
          .provider-grid{display:grid;grid-template-columns:repeat(auto-fill, minmax(170px, 1fr));gap:12px;justify-items:center}
          .provider-card{border:1px solid #e2e8f0;background:white;border-radius:6px;padding:10px;cursor:pointer;transition:border-color .15s,background .15s,transform .1s;display:flex;flex-direction:column;gap:8px;opacity:1;width:170px;min-height:170px;align-items:center;justify-content:flex-start;text-align:center;box-sizing:border-box}
          .provider-card:hover{border-color:#cbd5e1;transform:translateY(-1px)}
          .provider-card.active{border-color:var(--primary-500);box-shadow:0 0 0 1px var(--primary-500) inset}
          .provider-card.disabled{opacity:.6;cursor:not-allowed}
          .provider-top{display:flex;align-items:center;gap:8px}
          .dot{width:9px;height:9px;border-radius:50%;background:var(--primary-500,#0066ff)}
          .provider-logo{width:150px;height:130px;object-fit:contain;border-radius:6px;display:block;margin:0 auto}
          .provider-name{font-weight:600;font-size:13.5px;color:var(--text-primary)}
          .type-row{display:flex;gap:8px;flex-wrap:wrap}
          .type-pill{font-size:11.5px;letter-spacing:.03em;padding:9px 14px;border:1px solid #e2e8f0;border-radius:999px;background:white;color:var(--text-secondary);cursor:pointer;transition:all .15s}
          .type-pill.active{background:rgba(0,102,255,0.06);border-color:var(--primary-500);color:var(--primary-500)}
          .field-grid{display:grid;grid-template-columns:1fr 1fr;gap:18px 20px;margin-top:20px}
          .field-grid-3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:18px 20px;margin-top:20px}
          @media(max-width:900px){.provider-grid{grid-template-columns:repeat(2,1fr)}.field-grid{grid-template-columns:1fr}}
        `}</style>

        <SectionTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Button onClick={goBack} secondary>
              <FontAwesomeIcon icon={faChevronCircleLeft} />
            </Button>
            <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              {(model?.trim && model.trim()) || (displayName?.trim && displayName.trim()) || (isEditMode ? 'Edit Model' : 'Add New Model')}
            </span>
          </div>
        </SectionTitle>
        <Divider />

        <div className="panel">
          <div className="section-label"><span className="n">01</span> Provider</div>
          <div className="provider-grid">
            {Object.entries(PROVIDERS).map(([key, p]) => {
              const isActive = providerKey === key;
              return (
                <div
                  key={key}
                  className={`provider-card ${isActive ? 'active' : ''} ${p.disabled ? 'disabled' : ''}`}
                  onClick={() => !p.disabled && setProvider(p.label)}
                  title={p.disabled ? 'Coming soon' : p.label}
                >
                  {PROVIDER_ICONS[key] ? (
                    <img className="provider-logo" src={PROVIDER_ICONS[key]} alt={`${p.label} logo`} />
                  ) : (
                    <span className="dot" style={{ background: p.color }}></span>
                  )}
                  <div className="provider-name">{p.label}</div>
                </div>
              );
            })}
          </div>

          {hasProvider && (
            <>
              <div className="section-label" style={{ marginTop: 22 }}><span className="n">02</span> Model Type</div>
              <div className="type-row">
                {TYPE_OPTIONS.filter(t => !providerKey || supportsType(providerKey, t)).map(t => (
                  <div key={t} className={`type-pill ${type === t ? 'active' : ''}`} onClick={() => setType(t)}>{t}</div>
                ))}
              </div>
            </>
          )}

          {hasProvider && hasType && (
            <div className="field-grid-3">
              <div>
                <Label>Model Name<span style={{ color: '#6EA8FE' }}> *</span></Label>
                <Input type="text" value={model} onChange={(e) => setModel(e.target.value)} placeholder={providerKey==='azure' ? 'gpt-4o' : 'Claude Sonnet'} />
                <div style={{ fontSize: 12, color: '#8B93A7', marginTop: 6 }}>Resolves to provider base id via selected client</div>
              </div>
              <div>
                <Label>Display Name</Label>
                <Input type="text" value={displayName} onChange={(e)=>setDisplayName(e.target.value)} placeholder="Optional label shown in agent configs" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={status} onChange={(e)=>setStatus(e.target.value)}>
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </Select>
              </div>
            </div>
          )}

          {hasModel && (
            <div className="field-grid">
              <div>
                <Label>Deployment Name<span style={{ color: '#6EA8FE' }}> *</span></Label>
                <Input type="text" value={deploymentName} onChange={(e)=>setDeploymentName(e.target.value)} placeholder={providerKey==='azure' ? 'gpt-4o-prod-deploy' : 'unique-deploy-name'} />
                <div style={{ fontSize: 12, color: '#8B93A7', marginTop: 6 }}>Registry alias for this deployment</div>
              </div>
              <div>
                <Label>API version</Label>
                <Input type="text" value={version} onChange={(e)=>{ const v = e.target.value; setVersion(v); handleParamChange('api_version', v); }} placeholder={providerKey==='azure' ? '2024-10-21-preview' : '1.0.0'} />
              </div>
            </div>
          )}

          {hasModel && (
            <>
              <div style={{ display:'flex', alignItems:'center', gap:10, margin:'22px 0 4px', color:'#8B93A7', fontSize:12, textTransform:'uppercase', letterSpacing:'.12em' }}>Connection<div style={{ flex:1, height:1, background:'#212836' }} /></div>
              <div className="field-grid">
                <div>
                  <Label>API Key</Label>
                  <Input type="text" value={tempParamValues['api_key'] || ''} onChange={(e)=>handleParamChange('api_key', e.target.value)} placeholder="${AZURE_OPENAI_API_KEY}" />
                </div>
                <div>
                  <Label>Endpoint / Base URL<span style={{ color: '#6EA8FE' }}> *</span></Label>
                  <Input type="text" value={tempParamValues['base_url'] || tempParamValues['azure_endpoint'] || ''} onChange={(e)=>{ if(providerKey==='azure') handleParamChange('azure_endpoint', e.target.value); handleParamChange('base_url', e.target.value); }} placeholder={providerKey==='azure' ? 'https://your-resource.openai.azure.com' : (providerKey==='anthropic' ? 'https://api.anthropic.com' : '')} />
                  <div style={{ fontSize: 12, color: '#8B93A7', marginTop: 6 }}>{providerKey==='azure' ? 'Your Azure OpenAI resource endpoint.' : providerKey==='anthropic' ? 'Anthropic API endpoint or proxy.' : ''}</div>
                </div>
                
                {providerKey==='azure' && (
                  <div>
                    <Label>Organization</Label>
                    <Input type="text" value={tempParamValues['organization'] || ''} onChange={(e)=>handleParamChange('organization', e.target.value)} placeholder="org-... (optional)" />
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {hasModel && (
          <div className="panel">
            <div className="section-label" style={{ margin: 0 }}><span className="n">03</span> Model Parameters</div>
            <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:10 }}>
              <div style={{ marginLeft:'auto', display:'flex', gap:10, alignItems:'center' }}>
                <div style={{ position:'relative', maxWidth:230, width:'100%' }}>
                  <Input type="text" placeholder="Search parameters..." value={search} onChange={(e)=>setSearch(e.target.value)} />
                </div>
                <ButtonSuccess onClick={addCustomParam}><FontAwesomeIcon icon={faPlus} /> Add Param</ButtonSuccess>
              </div>
            </div>

            {filteredParams.length>0 ? (
              <Table>
                <thead>
                  <tr>
                    <TableHeader>Parameter</TableHeader>
                    <TableHeader>Value</TableHeader>
                    <TableHeader>Description</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {filteredParams.map((param) => (
                    <TableRow key={param.key}>
                      <TableCell>
                        <TableFormGroup>
                          <Input type="text" value={param.label||''} disabled={!param.key.startsWith('custom_')} onChange={(e)=>{ if(param.key.startsWith('custom_')) updateCustomLabel(param.key, e.target.value); }} />
                        </TableFormGroup>
                      </TableCell>
                      <TableCell>
                        {param.options ? (
                          <Select value={tempParamValues[param.key] ?? param.default ?? ''} onChange={(e)=>handleParamChange(param.key, e.target.value)}>
                            <option value="">Select</option>
                            {param.options.map(o => (<option key={o} value={o}>{o}</option>))}
                          </Select>
                        ) : param.kind==='bool' ? (
                          <input type="checkbox" checked={!!(tempParamValues[param.key] ?? param.default)} onChange={(e)=>handleParamChange(param.key, e.target.checked)} />
                        ) : (
                          <Input type={param.kind==='int' || param.kind==='float' ? 'number' : 'text'} value={tempParamValues[param.key] ?? param.default ?? ''} onChange={(e)=> handleParamChange(param.key, (param.kind==='int'||param.kind==='float') ? (e.target.value===''? '' : (param.kind==='int' ? parseInt(e.target.value,10) : parseFloat(e.target.value))) : e.target.value)} placeholder="Enter value" />
                        )}
                      </TableCell>
                      <TableCell>
                        <span style={{ color: 'var(--color-text-secondary)', fontSize: '13px' }}>{param.desc || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <Button onClick={()=> removeParam(param.key)} style={{ padding:'0.3rem 0.6rem', fontSize:'0.8rem', color:'white', backgroundColor:'#ff4444', borderColor:'#ff4444' }} title={param.key.startsWith('custom_') ? 'Remove parameter' : 'Clear value'}>
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div style={{ textAlign:'center', padding:'2rem', backgroundColor:'var(--color-background-secondary)', borderRadius:4, color:'var(--color-text-secondary)' }}>
                No parameters available for this selection.
              </div>
            )}
          </div>
        )}

        <Footer>
          <div style={{ flex: 1 }} />
          <Button onClick={goBack} style={{ marginRight: '0.5rem' }}>Cancel</Button>
          {hasModel && (
            <Button primary onClick={save}><FontAwesomeIcon icon={faSave} /> {isEditMode ? 'Update Model' : 'Create Model'}</Button>
          )}
        </Footer>
      </motion.div>
    </PageContainer>
  );
}
